import React, { Suspense, useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material'
import { default as CloseIcon } from '@mui/icons-material/Close'
import { default as CheckCircleIcon } from '@mui/icons-material/CheckCircle'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import {
  CustomInput,
  CustomSelect,
  CustomTextArea,
  selectTypes,
} from 'src/consumer/components/helperComponents'
import { ConfirmationModal } from 'src/consumer/components/helperComponents/dynamic'
import { putSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getPriceStringWithCurrency,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { currency_list, MOMENT_FORMATS } from 'src/consumer/constants'
import MultiplePictureUploadButton from 'src/consumer/components/Helpers/PictureUploadButton/MultiplePictureUploadButton'

const MarkAsPaidDialog = ({ open, onClose, invoice, onSuccess }) => {
  const [formData, setFormData] = useState({
    paidBy: '',
    paidOn: dayjs().format('YYYY-MM-DDTHH:mm'),
    comments: '',
    paymentTxnId: '',
    supportingImages: [],
  })

  const [loading, setLoading] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [errors, setErrors] = useState({})
  const [hasManuallyEditedAmtPaid, setHasManuallyEditedAmtPaid] = useState(false)

  // Calculate amtPaid based on retainerAmt and minimumPayout
  useEffect(() => {
    // Only auto-calculate if user hasn't manually edited the amount
    if (!hasManuallyEditedAmtPaid && formData.retainerAmt && formData.minimumPayout) {
      const generatedFee = invoice?.payoutAmountGenerated || 0
      const ideallyAmtPaid = Number(formData.retainerAmt) + Number(generatedFee)
      const minimumAmount = Number(formData.minimumPayout)

      const calculatedAmtPaid = ideallyAmtPaid < minimumAmount ? minimumAmount : ideallyAmtPaid

      setFormData((prev) => ({
        ...prev,
        amtPaid: calculatedAmtPaid,
      }))
    }
  }, [
    formData.retainerAmt,
    formData.minimumPayout,
    invoice?.payoutAmountGenerated,
    hasManuallyEditedAmtPaid,
  ])

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Mark as manually edited if user changes amtPaid
    if (field === 'amtPaid') {
      setHasManuallyEditedAmtPaid(true)
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.retainerAmt) {
      newErrors.retainerAmt = 'Retainer Amount is required'
    }

    if (!formData.minimumPayout) {
      newErrors.minimumPayout = 'Minimum Payout is required'
    }

    if (!formData.paidBy.trim()) {
      newErrors.paidBy = 'Paid By is required'
    }

    if (!formData.paidOn) {
      newErrors.paidOn = 'Paid On date is required'
    }

    if (!formData.amtPaid) {
      newErrors.amtPaid = 'Amount Paid is required'
    }

    if (!formData.paymentTxnId.trim()) {
      newErrors.paymentTxnId = 'Transaction ID is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = () => {
    if (validateForm()) {
      setShowConfirmation(true)
    }
  }

  // Handle payment update API call
  const handleConfirmSubmit = async () => {
    setLoading(true)
    setShowConfirmation(false)

    try {
      const paymentInfo = {
        paymentStatus: 'paid',
        paidBy: formData.paidBy,
        paidOn: dayjs(formData.paidOn).format(MOMENT_FORMATS.formatThatServerSends),
        comments: formData.comments,
        paymentTxnId: formData.paymentTxnId,
        supportingImages: formData.supportingImages,
        amtPaid: formData.amtPaid,
        currencyCode: formData.currencyCode,
        retainerAmt: formData.retainerAmt,
        minimumPayout: formData.minimumPayout,
      }

      const response = await putSPDataWithParams(
        `/api/secure/cc/support/invoices/payout-invoice/update-payment-details`,
        paymentInfo,
        { 'invoice-id': invoice.id },
      )

      if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
        toast.success('Payment details updated successfully')
        onSuccess() // Refresh the parent component
        handleClose()
      } else {
        toast.error(response.data?.message || 'Failed to update payment details')
      }
    } catch (error) {
      console.error('Error updating payment details:', error)
      toast.error('Failed to update payment details')
    } finally {
      setLoading(false)
    }
  }

  // Handle dialog close
  const handleClose = () => {
    if (!loading) {
      setFormData({
        paidBy: '',
        paidOn: dayjs().format('YYYY-MM-DDTHH:mm'),
        comments: '',
        paymentTxnId: '',
        supportingImages: [],
      })
      setErrors({})
      setHasManuallyEditedAmtPaid(false)
      onClose()
    }
  }

  // Handle image upload
  const handleAddImage = (imageId) => {
    setFormData((prev) => ({
      ...prev,
      supportingImages: [...prev.supportingImages, imageId],
    }))
  }

  // Handle image removal
  const handleRemoveImage = (imageId) => {
    setFormData((prev) => ({
      ...prev,
      supportingImages: prev.supportingImages.filter((id) => id !== imageId),
    }))
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={loading}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6">Mark Invoice as Paid</Typography>
            </Box>
            <IconButton onClick={handleClose} disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Invoice Information */}
          <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Invoice Details
            </Typography>
            <Typography variant="body2">
              <strong>Invoice Number:</strong> {invoice?.invoiceNumber}
            </Typography>
            <Typography variant="body2">
              <strong>Amount Period:</strong>{' '}
              {invoice?.invoiceFrom
                ? dayjs.utc(invoice.invoiceFrom).local().format('DD MMM YYYY')
                : '-'}{' '}
              to{' '}
              {invoice?.invoiceTo
                ? dayjs.utc(invoice.invoiceTo).local().format('DD MMM YYYY')
                : '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Amount Generated:</strong>{' '}
              {invoice?.payoutAmountGenerated
                ? getPriceStringWithCurrency(invoice.payoutAmountGenerated)
                : '--'}
            </Typography>
          </Box>

          {/* Payment Form */}
          <Grid container spacing={3}>
            {/* Retainer Amount and Minimum Payout first */}
            <Grid item xs={12} md={6}>
              <CustomInput
                label="Retainer Amount"
                type="number"
                value={formData.retainerAmt || null}
                onChange={(e) => handleChange('retainerAmt', e.target.value)}
                required
                invalid={!!errors.retainerAmt}
                invalidMessage={errors.retainerAmt}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInput
                label="Minimum Payout"
                type="number"
                value={formData.minimumPayout || null}
                onChange={(e) => handleChange('minimumPayout', e.target.value)}
                required
                invalid={!!errors.minimumPayout}
                invalidMessage={errors.minimumPayout}
              />
            </Grid>

            {/* Amount Paid (auto-calculated but editable) */}
            <Grid item xs={12} md={6}>
              <CustomInput
                label="Amount Paid"
                type="number"
                value={formData.amtPaid || null}
                onChange={(e) => handleChange('amtPaid', e.target.value)}
                required
                invalid={!!errors.amtPaid}
                invalidMessage={errors.amtPaid}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomSelect
                id="currencyCode"
                label="Currency Code"
                value={formData.currencyCode || null}
                selectType={selectTypes.string}
                onChangeEvent={(e) => handleChange('currencyCode', e.target.value)}
                options={currency_list
                  ?.filter((optn) => optn?.code !== 'unknown')
                  .map((currency) => ({
                    ...currency,
                    name: `(${currency.symbol}) ${currency.name}`,
                  }))}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.code}
                required
                invalid={!!errors.currencyCode}
                invalidMessage={errors.currencyCode}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CustomInput
                label="Paid By"
                value={formData.paidBy}
                onChange={(e) => handleChange('paidBy', e.target.value)}
                placeholder="Enter who made the payment"
                required
                invalid={!!errors.paidBy}
                invalidMessage={errors.paidBy}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CustomInput
                label="Paid On"
                type="datetime-local"
                value={formData.paidOn}
                onChange={(e) => handleChange('paidOn', e.target.value)}
                required
                invalid={!!errors.paidOn}
                invalidMessage={errors.paidOn}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomInput
                label="Transaction ID"
                value={formData.paymentTxnId}
                onChange={(e) => handleChange('paymentTxnId', e.target.value)}
                placeholder="Enter payment transaction ID"
                required
                invalid={!!errors.paymentTxnId}
                invalidMessage={errors.paymentTxnId}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextArea
                label="Comments"
                value={formData.comments}
                onChange={(e) => handleChange('comments', e.target.value)}
                placeholder="Enter any additional comments (optional)"
                rows={4}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 'medium' }}>
                Supporting Images (Optional)
              </Typography>
              <MultiplePictureUploadButton
                imageIds={formData.supportingImages}
                onAddImageId={handleAddImage}
                onDeleteImageId={handleRemoveImage}
                aspectWidth={400}
                aspectHeight={300}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
          >
            {loading ? 'Updating...' : 'Mark as Paid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal */}
      <Suspense fallback={<CircularProgress />}>
        <ConfirmationModal
          visibility={showConfirmation}
          visibilitySetter={setShowConfirmation}
          action="Update Payment"
        body={
          <div>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to mark this invoice as paid?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action will update the payment status.
            </Typography>
            <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
              <Typography variant="body2">
                <strong>Invoice:</strong> {invoice?.invoiceNumber}
              </Typography>
              <Typography variant="body2">
                <strong>Paid By:</strong> {formData.paidBy}
              </Typography>
              <Typography variant="body2">
                <strong>Transaction ID:</strong> {formData.paymentTxnId}
              </Typography>
            </Box>
          </div>
        }
        buttonColor="success"
        onSubmitFunctions={[handleConfirmSubmit]}
        />
      </Suspense>
    </>
  )
}

export default MarkAsPaidDialog
